import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Tag,
  Space,
  Button,
  Modal,
  message,
  Card,
  Statistic,
} from "antd";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import AgGridWithColumnState from "../../../../components/AgGridWithColumnState";
import { RollbackOutlined } from "@ant-design/icons";
import moment from "moment";
import {
  getOverpaymentAllocationHistory,
  rollbackOverpaymentAllocation,
} from "../../../api/accounting";
import { formatCurrencyWithConversion } from "../../../utils/currencyUtils";
import useDisplayCurrency from "../../../../../hooks/useDisplayCurrency";

const OverpaymentAllocationHistory = ({ selectedBrand }) => {
  // Global currency display - refreshKey changes when currency changes, forcing grid refresh
  const { refreshKey: currencyRefreshKey } = useDisplayCurrency();
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rollbackLoading, setRollbackLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const [dateFilter, setDateFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const gridApiRef = useRef(null);

  // Cache key for persistence
  const cacheKey = selectedBrand
    ? `overpayment-allocation-history-cache-${selectedBrand.id}`
    : null;

  // Load from cache on mount - use ref to avoid re-renders
  const cachedDataRef = useRef(null);
  const [hasLoadedFromCache, setHasLoadedFromCache] = useState(false);
  const fetchingRef = useRef(false); // Prevent multiple simultaneous requests
  const lastFetchKeyRef = useRef(null); // Track last fetch to prevent duplicates
  const fetchHistoryRef = useRef(null); // Store latest fetch function

  // Initialize cache on mount and when page changes
  useEffect(() => {
    if (!cacheKey) {
      cachedDataRef.current = null;
      setHasLoadedFromCache(false);
      return;
    }
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        const cacheAge = Date.now() - (parsed.timestamp || 0);
        if (cacheAge < 5 * 60 * 1000) {
          cachedDataRef.current = parsed.data;
          // Load current page from cache immediately if available
          const cacheKeyForPage = `page-${currentPage}-size-${pageSize}`;
          if (parsed.data && parsed.data[cacheKeyForPage]) {
            const cached = parsed.data[cacheKeyForPage];
            setHistoryData(cached.results || []);
            setTotalCount(cached.count || 0);
            setHasLoadedFromCache(true);
            setLoading(false);
            return;
          }
        } else {
          // Cache expired, clear it
          cachedDataRef.current = null;
        }
      }
    } catch (e) {
      console.warn("Failed to load cache:", e);
    }
    if (!cachedDataRef.current) {
      setHasLoadedFromCache(false);
    }
  }, [cacheKey, currentPage, pageSize]);

  // Fetch overpayment allocation history with pagination
  const fetchHistory = useCallback(
    async (page = currentPage, size = pageSize, forceRefresh = false) => {
      if (!selectedBrand) return;

      // Create unique key for this fetch request
      const fetchKey = `${selectedBrand.id}-${page}-${size}`;

      // Prevent duplicate simultaneous requests
      if (fetchingRef.current) {
        return;
      }

      // If we just fetched this exact same data, skip
      if (!forceRefresh && lastFetchKeyRef.current === fetchKey) {
        return;
      }

      // Check cache first if not forcing refresh
      const cacheKeyForPage = `page-${page}-size-${size}`;
      if (
        !forceRefresh &&
        cacheKey &&
        cachedDataRef.current &&
        cachedDataRef.current[cacheKeyForPage]
      ) {
        const cached = cachedDataRef.current[cacheKeyForPage];
        setHistoryData(cached.results || []);
        setTotalCount(cached.count || 0);
        setLoading(false);
        lastFetchKeyRef.current = fetchKey;
        return;
      }

      // Mark as fetching
      fetchingRef.current = true;
      lastFetchKeyRef.current = fetchKey;

      try {
        setLoading(true);
        const response = await getOverpaymentAllocationHistory({
          brand: selectedBrand.id,
          page: page,
          page_size: size,
          date: dateFilter,
        });

        // Handle different response formats
        let pageResults = [];
        let pageCount = 0;

        if (response?.data?.results) {
          pageResults = response.data.results;
          pageCount = response.data.count || 0;
        } else if (response?.results) {
          pageResults = response.results;
          pageCount = response.count || response.results.length;
        } else if (Array.isArray(response)) {
          pageResults = response;
          pageCount = response.length;
        } else if (response?.data && Array.isArray(response.data)) {
          pageResults = response.data;
          pageCount = response.data.length;
        }

        setHistoryData(pageResults);
        setTotalCount(pageCount || pageResults.length);

        // Cache the results
        if (cacheKey) {
          try {
            const cacheData = cachedDataRef.current || {};
            cacheData[`page-${page}-size-${size}`] = {
              results: pageResults,
              count: pageCount || pageResults.length,
              timestamp: Date.now(),
            };
            cachedDataRef.current = cacheData;
            localStorage.setItem(
              cacheKey,
              JSON.stringify({ data: cacheData, timestamp: Date.now() })
            );
          } catch (e) {
            console.warn("Failed to save cache:", e);
          }
        }

        // Update filtered count after data is set
        setTimeout(() => {
          if (gridApiRef.current) {
            let filtered = 0;
            gridApiRef.current.forEachNodeAfterFilter(() => {
              filtered++;
            });
            setFilteredCount(filtered);
          }
        }, 100);
      } catch (error) {
        console.error("Error fetching overpayment allocation history:", error);
        message.error("Failed to load overpayment allocation history");
        setHistoryData([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
        fetchingRef.current = false;
      }
    },
    [selectedBrand, dateFilter, currentPage, pageSize, cacheKey]
  );

  // Keep ref updated with latest fetchHistory
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  // Handle pagination changes
  const handlePaginationChange = useCallback(
    (newPage, newPageSize) => {
      setCurrentPage(newPage);
      if (newPageSize !== pageSize) {
        setPageSize(newPageSize);
      }
    },
    [pageSize]
  );

  // Fetch overpayment allocation history when selected brand or pagination changes
  // Only fetch if we haven't loaded from cache or if brand/page changed
  useEffect(() => {
    if (!selectedBrand) {
      setHistoryData([]);
      setTotalCount(0);
      setHasLoadedFromCache(false);
      lastFetchKeyRef.current = null;
      return;
    }

    // If we just loaded from cache, don't fetch again
    if (hasLoadedFromCache) {
      setHasLoadedFromCache(false);
      return;
    }

    // Small delay to batch multiple rapid changes
    const timeoutId = setTimeout(() => {
      if (!fetchingRef.current && fetchHistoryRef.current) {
        fetchHistoryRef.current(currentPage, pageSize, false);
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedBrand, currentPage, pageSize]);

  // Handle rollback to a previous version
  const handleRollback = (historyId, recordDate) => {
    Modal.confirm({
      title: "Confirm Rollback",
      content: `Are you sure you want to roll back this overpayment allocation to the version from ${moment(
        recordDate
      ).format("YYYY-MM-DD HH:mm:ss")}?`,
      okText: "Yes, Roll Back",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          setRollbackLoading(true);
          const result = await rollbackOverpaymentAllocation(
            historyId,
            recordDate
          );
          // Handle different response structures
          const successMsg =
            result?.message ||
            result?.data?.message ||
            "Overpayment allocation rolled back successfully";
          message.success(successMsg);
          // Refresh the history data
          fetchHistory(currentPage, pageSize, true);
        } catch (err) {
          // Handle different error response structures - fix error message
          const errorMsg =
            err.response?.data?.error ||
            err.response?.data?.detail ||
            err.response?.data?.message ||
            err.message ||
            "Unknown error";
          // Replace incorrect "Cash allocation not found" with correct message
          const correctedMsg = errorMsg.includes("Cash allocation")
            ? errorMsg.replace("Cash allocation", "Overpayment allocation")
            : errorMsg;
          message.error(
            "Failed to roll back overpayment allocation: " + correctedMsg
          );
          console.error("Rollback error:", err);
        } finally {
          setRollbackLoading(false);
        }
      },
    });
  };

  // Format the history type for display
  const formatHistoryType = (type) => {
    switch (type) {
      case "+":
        return <Tag color="green">Created</Tag>;
      case "-":
        return <Tag color="red">Deleted</Tag>;
      case "~":
        return <Tag color="blue">Updated</Tag>;
      default:
        return <Tag>{type}</Tag>;
    }
  };

  // AG Grid column definitions
  const columnDefs = [
    {
      headerName: "Date",
      field: "history_date",
      sortable: true,
      filter: true,
      width: 180,
      valueFormatter: (params) => {
        return params.value
          ? moment(params.value).format("YYYY-MM-DD HH:mm:ss")
          : "";
      },
    },
    {
      headerName: "Overpayment",
      field: "overpayment_number",
      sortable: true,
      filter: true,
      width: 180,
      valueGetter: (params) => {
        const data = params.data;
        // Try multiple possible field names for overpayment reference
        return (
          data?.overpayment_number ||
          data?.overpayment_reference ||
          data?.source_overpayment_number ||
          (data?.overpayment_id ? `OP-${data.overpayment_id}` : null) ||
          (data?.overpayment ? `OP-${data.overpayment}` : null) ||
          (data?.source_overpayment_id
            ? `OP-${data.source_overpayment_id}`
            : null) ||
          (data?.id ? `Allocation #${data.id}` : "-")
        );
      },
    },
    {
      headerName: "Allocated To",
      field: "allocation_target",
      sortable: true,
      filter: true,
      width: 200,
      cellRenderer: (params) => {
        const data = params.data;

        // Check if allocated to an invoice - try multiple field names
        const invoiceNumber = data?.invoice_number || data?.invoice_ref;
        const invoiceId = data?.invoice_id || data?.invoice;

        if (invoiceNumber) {
          return (
            <span>
              <Tag color="blue">Invoice</Tag> {invoiceNumber}
            </span>
          );
        }

        // Check if allocated to another overpayment - try multiple field names
        const targetOpNumber =
          data?.target_overpayment_number ||
          data?.target_overpayment_reference ||
          data?.allocated_to_overpayment_number;
        const targetOpId =
          data?.target_overpayment_id ||
          data?.target_overpayment ||
          data?.allocated_to_overpayment;

        if (targetOpNumber) {
          return (
            <span>
              <Tag color="orange">Overpayment</Tag> {targetOpNumber}
            </span>
          );
        }

        // Fallback to IDs if numbers not available
        if (invoiceId) {
          return (
            <span>
              <Tag color="blue">Invoice</Tag> #{invoiceId}
            </span>
          );
        }
        if (targetOpId) {
          return (
            <span>
              <Tag color="orange">Overpayment</Tag> OP-{targetOpId}
            </span>
          );
        }
        return "-";
      },
    },
    {
      headerName: "Amount",
      field: "amount",
      sortable: true,
      filter: "agNumberColumnFilter",
      width: 120,
      cellRenderer: (params) => {
        if (!params.value)
          return formatCurrencyWithConversion(0, params.data?.currency || "$");
        const currency = params.data?.currency || "$";
        return formatCurrencyWithConversion(params.value, currency);
      },
    },
    {
      headerName: "Allocation Date",
      field: "allocation_date",
      sortable: true,
      filter: true,
      width: 140,
      valueFormatter: (params) => {
        return params.value ? moment(params.value).format("YYYY-MM-DD") : "";
      },
    },
    {
      headerName: "Action",
      field: "history_type",
      sortable: true,
      filter: true,
      width: 100,
      cellRenderer: (params) => {
        const type = params.value;
        if (type === "+") {
          return <Tag color="green">Created</Tag>;
        } else if (type === "~") {
          return <Tag color="blue">Updated</Tag>;
        } else if (type === "-") {
          return <Tag color="red">Deleted</Tag>;
        }
        return <Tag>{type}</Tag>;
      },
    },
    {
      headerName: "User",
      field: "history_username",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      headerName: "Changes",
      field: "field_changes",
      sortable: false,
      filter: false,
      width: 200,
      cellRenderer: (params) => {
        const changes = params.value;
        if (!changes || Object.keys(changes).length === 0) {
          return <Tag color="default">No changes</Tag>;
        }
        if (changes.created) {
          return <Tag color="green">Overpayment allocation created</Tag>;
        }
        if (changes.deleted) {
          return <Tag color="red">Overpayment allocation deleted</Tag>;
        }
        return <Tag color="default">Modified</Tag>;
      },
    },
    {
      headerName: "Change Reason",
      field: "history_change_reason",
      sortable: true,
      filter: true,
      width: 150,
      valueFormatter: (params) => {
        return params.value || "N/A";
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      width: 120,
      pinned: "right",
      cellRenderer: (params) => {
        const isDeletion = params.data.history_type === "-";
        return (
          <Button
            type="primary"
            icon={<RollbackOutlined />}
            size="small"
            onClick={() =>
              handleRollback(params.data.history_id, params.data.history_date)
            }
            loading={rollbackLoading}
            disabled={isDeletion}
          >
            Rollback
          </Button>
        );
      },
    },
  ];

  // Default column definition
  const defaultColDef = {
    sortable: true,
    filter: true,
    resizable: true,
  };

  // Update filtered count when filters change
  const updateFilteredCount = useCallback(() => {
    if (gridApiRef.current) {
      let count = 0;
      gridApiRef.current.forEachNodeAfterFilter(() => {
        count++;
      });
      setFilteredCount(count);
    }
  }, []);

  return (
    <Card
      title="Overpayment Allocation History"
      style={{ height: "100%" }}
      extra={
        <Space size="large">
          <Statistic
            title="Total Allocations"
            value={totalCount}
            valueStyle={{ fontSize: 18, fontWeight: "bold" }}
          />
          {filteredCount !== totalCount && (
            <Statistic
              title="Filtered"
              value={filteredCount}
              valueStyle={{
                fontSize: 18,
                fontWeight: "bold",
                color: "#1890ff",
              }}
            />
          )}
        </Space>
      }
    >
      <div
        className="ag-theme-alpine"
        style={{ height: "600px", width: "100%" }}
      >
        <AgGridWithColumnState
          key={`overpayment-allocation-history-${currencyRefreshKey}`}
          tableId="overpayment-allocation-history"
          gridApiRef={gridApiRef}
          rowData={historyData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination={true}
          paginationPageSize={pageSize}
          paginationPageSizeSelector={[25, 50, 100, 200, 500]}
          paginationPosition="top"
          onPaginationChanged={(params) => {
            if (params.newPage !== undefined) {
              const newPage = params.api.paginationGetCurrentPage() + 1;
              const newPageSize = params.api.paginationGetPageSize();
              if (newPage !== currentPage || newPageSize !== pageSize) {
                handlePaginationChange(newPage, newPageSize);
              }
            }
          }}
          domLayout="normal"
          loading={loading}
          getRowId={(params) => params.data.history_id}
          onGridReady={(params) => {
            gridApiRef.current = params.api;
            updateFilteredCount();
          }}
          onFilterChanged={updateFilteredCount}
          onSortChanged={updateFilteredCount}
        />
      </div>
    </Card>
  );
};

export default OverpaymentAllocationHistory;
